import { useMemo } from 'react';
import { cn } from '@/lib/utils';

const PALETTES: Array<[string, string]> = [
  ['#22d3ee', '#a855f7'], // cyan → violet (brand)
  ['#06b6d4', '#3b82f6'], // teal → blue
  ['#a855f7', '#ec4899'], // violet → pink
  ['#10b981', '#22d3ee'], // emerald → cyan
  ['#f59e0b', '#ef4444'], // amber → rose
  ['#8b5cf6', '#22d3ee'], // purple → cyan
  ['#0ea5e9', '#a855f7'], // sky → violet
  ['#14b8a6', '#22d3ee'], // teal → cyan
];

function hashSeed(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(31, h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export interface AvatarProps {
  seed: string;             // phone number, customer id — anything stable
  name?: string | null;     // used to derive initials
  size?: number;            // px
  className?: string;
  square?: boolean;
}

/**
 * Deterministic, pretty avatar. Same seed → same gradient. Initials are
 * pulled from `name` (falls back to first two seed chars).
 */
export function Avatar({ seed, name, size = 40, className, square = false }: AvatarProps) {
  const { from, to, initials } = useMemo(() => {
    const h = hashSeed(seed || 'anon');
    const [from, to] = PALETTES[h % PALETTES.length];

    let initials = '';
    if (name && name.trim()) {
      const parts = name.trim().split(/\s+/);
      initials = (parts[0][0] || '') + (parts[1]?.[0] || '');
    }
    if (!initials) {
      const cleaned = (seed || '').replace(/[^a-z0-9]/gi, '');
      initials = (cleaned.slice(0, 2) || '··').toUpperCase();
    }
    return { from, to, initials: initials.toUpperCase().slice(0, 2) };
  }, [seed, name]);

  return (
    <span
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${from}, ${to})`,
      }}
      className={cn(
        'relative inline-grid shrink-0 place-items-center font-semibold text-zinc-950 ring-1 ring-white/10',
        square ? 'rounded-xl' : 'rounded-full',
        className,
      )}
      aria-hidden
    >
      <span
        style={{ fontSize: Math.max(11, size * 0.38) }}
        className="select-none drop-shadow-[0_1px_0_rgba(255,255,255,0.3)]"
      >
        {initials}
      </span>
      <span className="pointer-events-none absolute inset-0 rounded-[inherit] bg-gradient-to-b from-white/15 to-transparent" />
    </span>
  );
}
